import { Dropdown, Label, makeStyles, Option, shorthands, tokens } from "@fluentui/react-components";
import { GridColDef } from "@mui/x-data-grid";
import { useCallback, useMemo } from "react";
import { CustomShimmer } from "../../../../helper/CustomShimmer";
import { formatDate } from "../../../../helper/DateFormat";
import { CustomDataGrid } from "../../../../SharedComponents/CustomDataGrid/CustomDataGrid";
import { CustomLinkNavigate } from "../../../../SharedComponents/CustomLinkNavigate";
import { CustomMetricsCard } from "../../../../SharedComponents/CustomMetricsCard";
import { PRODUCT_VIEW_STYLES } from "../../Common/ProductView.styles";
import MultiSeriesTrendChart from "./MultiSeriesTrendChart";
import PartnerTenantsChart from "./PartnerTenantsChart";
import {
  EntraEliteFilter,
  PartnerInfluenceData
} from "./partnerInfluence.interfaces";

interface MetricDefinition {
  color: string;
  key: string;
  label: string;
}

const ENTRA_ID_METRICS: MetricDefinition[] = [
  { key: "pau", label: "PAU", color: "#009C88" },
  { key: "e5Pau", label: "E5 PAU", color: "#D13438" },
  { key: "e3Pau", label: "E3 PAU", color: "#C19C00" },
  { key: "mau", label: "MAU", color: "#D61F69" },
  { key: "caMau", label: "CA MAU", color: "#0388C9" },
  { key: "rbcaMau", label: "RBCA MAU", color: "#E66C00" },
  { key: "e5Mau", label: "E5 MAU", color: "#9238B2" },
  { key: "logins", label: "Logins", color: "#145BD6" },
  { key: "e5Logins", label: "E5 Logins", color: "#C129A3" }
];

const ENTRA_IG_METRICS: MetricDefinition[] = [
  { key: "pau", label: "PAU", color: "#009C88" },
  { key: "au", label: "AU", color: "#0388C9" },
  { key: "lcwUsage", label: "LCW Usage", color: "#D61F69" },
  { key: "em", label: "EM", color: "#E66C00" },
  { key: "ar", label: "AR", color: "#9238B2" }
];

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalL),
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL)
  },
  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(128px, 1fr))",
    ...shorthands.gap(tokens.spacingHorizontalS)
  },
  compactMetricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(112px, 1fr))",
    ...shorthands.gap(tokens.spacingHorizontalS)
  },
  chartsRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    ...shorthands.gap(tokens.spacingHorizontalL),
    "@media (min-width: 1100px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))"
    }
  },
  bottomRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    ...shorthands.gap(tokens.spacingHorizontalL),
    "@media (min-width: 1200px)": {
      gridTemplateColumns: "minmax(420px, 1fr) minmax(620px, 1.4fr)"
    }
  },
  gridSection: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL)
  },
  gridHeader: {
    alignItems: "end",
    display: "flex",
    justifyContent: "space-between",
    ...shorthands.gap(tokens.spacingHorizontalM),
    marginBottom: tokens.spacingVerticalM
  },
  gridTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold
  },
  filterField: {
    minWidth: "180px"
  },
  gridContainer: {
    height: "470px",
    width: "100%"
  }
});

interface TrendViewPoint {
  snapshotDate: Date;
  snapshotYearMonth: number;
  usage: Record<string, number>;
  partnerCounts: Record<string, number>;
  totalPartners: number;
  cporTenants: number;
  avgCustomersPerPartner: number;
}

interface EntraGridRow {
  id: string;
  partnerOneId: string;
  partnerOneName: string;
  isElite: boolean;
  partnerCountry: string;
  pau: number;
  snapshotDate: Date | null;
  snapshotYearMonth: number;
  e5Pau?: number;
  e3Pau?: number;
  mau?: number;
  caMau?: number;
  rbcaMau?: number;
  e5Mau?: number;
  logins?: number;
  e5Logins?: number;
  customersCount?: number;
  customersWithPAU?: number;
  au?: number;
  lcwUsage?: number;
  em?: number;
  ar?: number;
  tenants?: number;
}

const toNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined) return 0;
  const parsed = typeof value === "number" ? value : Number(value.replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCompact = (value: number): string =>
  new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 }).format(value);

const navigateToPartner = (partnerOneId: string) =>
  `/partners?partnerOneId=${encodeURIComponent(partnerOneId)}&mpnId=AllUp`;

const numberColumn = (headerName: string, field: keyof EntraGridRow, minWidth = 125): GridColDef<EntraGridRow> => ({
  field,
  headerName,
  minWidth,
  type: "number",
  valueFormatter: (value: number) => toNumber(value).toLocaleString("en-US", { maximumFractionDigits: 0 })
});

const EntraIDPartnerInfluenceTab = ({
  data,
  eliteFilter,
  isEntraIG = false,
  isLoading,
  onEliteFilterChange
}: {
  data?: PartnerInfluenceData;
  eliteFilter: EntraEliteFilter;
  isEntraIG?: boolean;
  isLoading: boolean;
  onEliteFilterChange: (value: EntraEliteFilter) => void;
}) => {
  const styles = useStyles();
  const getRowId = useCallback((row: EntraGridRow) => row.id, []);
  const metrics = isEntraIG ? ENTRA_IG_METRICS : ENTRA_ID_METRICS;
  const productDisplayName = isEntraIG ? "Microsoft Entra Identity Governance" : "Microsoft Entra ID";

  const trendData = useMemo<TrendViewPoint[]>(
    () => isEntraIG
      ? (data?.entraIGTrendDetails ?? []).map((item) => ({
          snapshotDate: new Date(item.snapshotDate),
          snapshotYearMonth: item.snapshotYearMonth,
          usage: {
            pau: toNumber(item.pauPartnerInfluenced),
            au: toNumber(item.auPartnerInfluenced),
            lcwUsage: toNumber(item.lcwUsagePartnerInfluenced),
            em: toNumber(item.emPartnerInfluenced),
            ar: toNumber(item.arPartnerInfluenced)
          },
          partnerCounts: {
            pau: toNumber(item.partnersWithPau),
            au: toNumber(item.partnersWithAu),
            lcwUsage: toNumber(item.partnersWithLcwUsage),
            em: toNumber(item.partnersWithEm),
            ar: toNumber(item.partnersWithAr)
          },
          totalPartners: toNumber(item.totalPartners),
          cporTenants: toNumber(item.cporTenants),
          avgCustomersPerPartner: toNumber(item.avgCustomersPerPartner)
        }))
      : (data?.entraTrendDetails ?? []).map((item) => ({
          snapshotDate: new Date(item.snapshotDate),
          snapshotYearMonth: item.snapshotYearMonth,
          usage: {
            pau: toNumber(item.pauPartnerInfluenced),
            e5Pau: toNumber(item.e5PauPartnerInfluenced),
            e3Pau: toNumber(item.e3PauPartnerInfluenced),
            mau: toNumber(item.mauPartnerInfluenced),
            caMau: toNumber(item.caMauPartnerInfluenced),
            rbcaMau: toNumber(item.rbcaMauPartnerInfluenced),
            e5Mau: toNumber(item.e5MauPartnerInfluenced),
            logins: toNumber(item.loginsPartnerInfluenced),
            e5Logins: toNumber(item.e5LoginsPartnerInfluenced)
          },
          partnerCounts: {
            pau: toNumber(item.partnersWithPau),
            e5Pau: toNumber(item.partnersWithE5Pau),
            e3Pau: toNumber(item.partnersWithE3Pau),
            mau: toNumber(item.partnersWithMau),
            caMau: toNumber(item.partnersWithCaMau),
            rbcaMau: toNumber(item.partnersWithRbcaMau),
            e5Mau: toNumber(item.partnersWithE5Mau),
            logins: toNumber(item.partnersWithLogins),
            e5Logins: toNumber(item.partnersWithE5Logins)
          },
          totalPartners: toNumber(item.totalPartners),
          cporTenants: toNumber(item.cporTenants),
          avgCustomersPerPartner: toNumber(item.avgCustomersPerPartner)
        })),
    [data?.entraIGTrendDetails, data?.entraTrendDetails, isEntraIG]
  );

  const latestTrend = useMemo(
    () => trendData.reduce<TrendViewPoint | undefined>((latest, item) =>
      !latest || item.snapshotDate > latest.snapshotDate ? item : latest, undefined),
    [trendData]
  );

  const usageSeries = useMemo(
    () => metrics.map((metric) => ({
      color: metric.color,
      legend: metric.label,
      getValue: (point: TrendViewPoint) => toNumber(point.usage[metric.key])
    })),
    [metrics]
  );

  const partnerCountSeries = useMemo(
    () => metrics.map((metric) => ({
      color: metric.color,
      legend: `Partners with ${metric.label}`,
      getValue: (point: TrendViewPoint) => toNumber(point.partnerCounts[metric.key])
    })),
    [metrics]
  );

  const partnerTrendData = useMemo(
    () => trendData.map((item) => ({
      snapshotDate: item.snapshotDate,
      partners_IsElite: toNumber(item.totalPartners),
      partners_NonElite: 0,
      tenantCount: toNumber(item.cporTenants),
      avgCustomersbyPartners: toNumber(item.avgCustomersPerPartner)
    })),
    [trendData]
  );

  const rows = useMemo<EntraGridRow[]>(
    () => isEntraIG
      ? (data?.entraIGPartnerInfluenceDetails ?? []).map((item, index) => ({
          ...item,
          id: `${item.partnerOneId}-${item.snapshotYearMonth}-${index}`,
          snapshotDate: item.snapshotDate ? new Date(item.snapshotDate) : null,
          pau: toNumber(item.pau),
          au: toNumber(item.au),
          lcwUsage: toNumber(item.lcwUsage),
          em: toNumber(item.em),
          ar: toNumber(item.ar),
          tenants: toNumber(item.tenants)
        }))
      : (data?.entraPartnerInfluenceDetails ?? []).map((item, index) => ({
          ...item,
          id: `${item.partnerOneId}-${item.snapshotYearMonth}-${index}`,
          snapshotDate: item.snapshotDate ? new Date(item.snapshotDate) : null,
          pau: toNumber(item.pau),
          e5Pau: toNumber(item.e5Pau),
          e3Pau: toNumber(item.e3Pau),
          mau: toNumber(item.mau),
          caMau: toNumber(item.caMau),
          rbcaMau: toNumber(item.rbcaMau),
          e5Mau: toNumber(item.e5Mau),
          logins: toNumber(item.logins),
          e5Logins: toNumber(item.e5Logins),
          customersCount: toNumber(item.customersCount),
          customersWithPAU: toNumber(item.customersWithPAU)
        })),
    [data?.entraIGPartnerInfluenceDetails, data?.entraPartnerInfluenceDetails, isEntraIG]
  );

  const columns = useMemo<GridColDef<EntraGridRow>[]>(() => {
    const identityColumns: GridColDef<EntraGridRow>[] = [
      { field: "partnerOneId", headerName: "Partner One ID", minWidth: 150 },
      {
        field: "partnerOneName",
        headerName: "Partner Name",
        minWidth: 190,
        renderCell: ({ row }) => <CustomLinkNavigate to={navigateToPartner(row.partnerOneId)} text={row.partnerOneName} />
      },
      { field: "isElite", headerName: "Is Elite", minWidth: 100, type: "boolean" },
      { field: "partnerCountry", headerName: "Partner Country", minWidth: 160 }
    ];

    if (isEntraIG) {
      return [
        ...identityColumns,
        numberColumn("PAU", "pau"),
        numberColumn("AU", "au"),
        numberColumn("LCW Usage", "lcwUsage", 145),
        numberColumn("EM", "em"),
        numberColumn("AR", "ar"),
        numberColumn("Tenants", "tenants")
      ];
    }

    return [
      ...identityColumns,
      numberColumn("PAU", "pau"),
      numberColumn("E5 PAU", "e5Pau"),
      numberColumn("E3 PAU", "e3Pau"),
      numberColumn("MAU", "mau"),
      numberColumn("CA MAU", "caMau"),
      numberColumn("RBCA MAU", "rbcaMau", 145),
      numberColumn("E5 MAU", "e5Mau"),
      numberColumn("Logins", "logins"),
      numberColumn("E5 Logins", "e5Logins"),
      numberColumn("Customers", "customersCount", 135),
      numberColumn("Customers with PAU", "customersWithPAU", 180),
      {
        field: "snapshotDate",
        headerName: "Snapshot Date",
        minWidth: 150,
        type: "date",
        valueFormatter: (value: Date | null) => value ? formatDate(value.toISOString()) : "-"
      }
    ];
  }, [isEntraIG]);

  const renderMetricCards = (showPartnerCounts: boolean) => metrics.map((metric) => {
    const value = latestTrend
      ? toNumber((showPartnerCounts ? latestTrend.partnerCounts : latestTrend.usage)[metric.key])
      : 0;
    const title = showPartnerCounts ? `Partners with ${metric.label}` : metric.label;
    return (
      <CustomMetricsCard
        key={`${showPartnerCounts ? "partners" : "usage"}-${metric.key}`}
        title={title}
        description={`${title} for the latest available ${productDisplayName} snapshot.`}
        currentValue={value.toLocaleString("en-US", { maximumFractionDigits: 0 })}
        abbrevatedValue={formatCompact(value)}
        color={metric.color}
      />
    );
  });

  const renderMetricRow = (showPartnerCounts: boolean) => {
    if (isLoading) {
      return <CustomShimmer rowCount={1} columnCount={metrics.length} styles={PRODUCT_VIEW_STYLES.OVERVIEW_SECTOIN.shimmerStyles} />;
    }

    return <div className={styles.metricGrid}>{renderMetricCards(showPartnerCounts)}</div>;
  };

  return (
    <div className={styles.root}>
      {isEntraIG ? (
        isLoading
          ? <CustomShimmer rowCount={1} columnCount={10} styles={PRODUCT_VIEW_STYLES.OVERVIEW_SECTOIN.shimmerStyles} />
          : <div className={styles.compactMetricGrid}>{renderMetricCards(false)}{renderMetricCards(true)}</div>
      ) : (
        <>
          {renderMetricRow(false)}
          {renderMetricRow(true)}
        </>
      )}

      <div className={styles.chartsRow}>
        <MultiSeriesTrendChart
          cardTitle="How are CPOR usage and licenses trending?"
          data={trendData}
          isLoading={isLoading}
          kustoLink={data?.usageLicenseKustoLink}
          series={usageSeries}
        />
        <MultiSeriesTrendChart
          cardTitle="How are CPOR partners with usage and licenses trending?"
          data={trendData}
          isLoading={isLoading}
          kustoLink={data?.usageLicenseKustoLink}
          series={partnerCountSeries}
        />
      </div>

      <div className={styles.bottomRow}>
        <PartnerTenantsChart
          cardTitle="Partners & CPOR Tenants Trend"
          colors={{ partnersIsElite: "#0F7CD5", tenants: "#00BFA5", avgCustomers: "#E3005B" }}
          combinePartners
          data={partnerTrendData}
          groupBars
          isLoading={isLoading}
          kustoLink={data?.partnerTenantKustoLink}
          legends={{ partnersIsElite: "Partners", tenants: "CPOR Tenants", avgCustomers: "Avg Customers/Partners" }}
        />

        <section className={styles.gridSection} aria-labelledby="entra-partners-grid-title">
          <div className={styles.gridHeader}>
            <span id="entra-partners-grid-title" className={styles.gridTitle}>Top 200 Partners</span>
            {!isEntraIG && (
              <div className={styles.filterField}>
                <Label htmlFor="entra-elite-filter">Partner segment</Label>
                <Dropdown
                  id="entra-elite-filter"
                  selectedOptions={[eliteFilter]}
                  value={eliteFilter === "All" ? "All partners" : eliteFilter === "True" ? "Elite only" : "Non-Elite only"}
                  onOptionSelect={(_event, option) => onEliteFilterChange(option.optionValue as EntraEliteFilter)}
                >
                  <Option value="All">All partners</Option>
                  <Option value="True">Elite only</Option>
                  <Option value="False">Non-Elite only</Option>
                </Dropdown>
              </div>
            )}
          </div>
          <div className={styles.gridContainer}>
            <CustomDataGrid
              columns={columns}
              exportCsvButtonProps={{
                fileNamePrefix: isEntraIG
                  ? "entra-ig-partner-influence"
                  : `entra-id-partner-influence-${eliteFilter.toLowerCase()}`
              }}
              getRowId={getRowId}
              kustoLink={data?.partnerInfluenceKustoLink}
              loading={isLoading}
              paginationOptions={{ page: 0, pageSize: 25 }}
              rows={{ values: rows, count: rows.length }}
              showCellVerticalBorder
              TabName={isEntraIG
                ? "Product View - Entra IG Partner Influence"
                : `Product View - Entra ID Partner Influence - ${eliteFilter}`}
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default EntraIDPartnerInfluenceTab;
